/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { BaseComponent } from '../base.component';
import { ContentInjectionService, LoadingService } from '../../services';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-action-dots',
    templateUrl: './action-dots.component.html',
    styleUrls: ['./action-dots.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ActionDotsComponent extends BaseComponent implements AfterViewInit {
    /**
     * Reference to DOM ng-content
     */
    @ViewChild('contentWrapper', { static: true })
    public content: ElementRef;

    /**
     * Reference to template.
     */
    @ViewChild('templateWrapper', { static: true })
    public templateWrapper: TemplateRef<any>;

    /**
     * DOM reference to action dots container.
     */
    @ViewChild('actions')
    public actions: ElementRef;

    /**
     * Set a unique id on the action dots object.
     */
    @Input() public id: string = 'action-dots';

    /**
     * Alternative action dot button image.
     */
    @Input() public triggerButtonImagePath: string = './assets/shared/icons/action-dots.svg';

    /**
     * Sets _isLoading to states value.
     * @param state
     */
    @Input() set isLoading(state: boolean) {
        this._isLoading = state;
    }

    /**
     * Change the display of the action dots.
     */
    @Input() public displayAsButton: boolean = false;

    /**
     * When styled as a button apply the following class.
     */
    @Input() public actionDotsClass: string = 'outline';

    /**
     * Is button split.
     */
    @Input() public splitAction: boolean = false;

    /**
     * Set the content container width.
     */
    @Input() public contentWidth: number = 250;

    /**
     * Show caret flag.
     */
    @Input() public showCaret: boolean = false;

    /**
     * Output when an action dot is clicked.
     */
    @Output() public actionDotChildClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * Sets isOpen to false.
     * @private
     */
    private isOpen: boolean = false;

    /**
     * Sets _isLoading to false.
     * @private
     */
    private _isLoading: boolean = false;

    constructor(
        private cdr: ChangeDetectorRef,
        private contentInjectionService: ContentInjectionService,
        private loadingService: LoadingService,
    ) {
        super();
    }

    /**
     * Is loading getter
     */
    get isLoading(): boolean {
        return this._isLoading;
    }

    /**
     * Angular hook
     */
    public ngAfterViewInit() {
        this.setupSubscribers();
    }

    /**
     * Check if the image is external
     */
    public usingExternalImage(): boolean {
        return this.triggerButtonImagePath !== './assets/shared/icons/action-dots.svg';
    }

    /**
     * Toggles the action dots visibility and add/remove the click handler.
     * @param $event
     */
    public toggleActionDots($event: any): void {
        this.isOpen = !this.isOpen;

        if (this.isOpen) {
            const overlayContentState = this.createOverlayContentState($event);
            this.contentInjectionService.openEmitter.next(overlayContentState);
        } else {
            this.contentInjectionService.closeEmitter.next(true);
        }
    }

    /**
     * Create the action dots template.
     * @param $event
     * @private
     */
    private createOverlayContentState($event: any): any {
        const refRect = this.actions.nativeElement.getBoundingClientRect();
        const isSplitAction: boolean = this.splitAction || this.displayAsButton;

        return {
            id: this.id,
            open: this.isOpen,
            template: this.content,
            containerWidth: this.contentWidth,
            top: refRect.y + (isSplitAction ? 36 : 8),
            bottom: refRect.bottom - 7,
            right: this.getXPosition(refRect),
            event: $event,
            isSplitAction: isSplitAction,
            showCaret: this.showCaret,
            overlay: true,
            overlaySelector: 'actions-content'
        };
    }

    /**
     * Get x position of action dots container based on action dot type e.g. when displayed as a button, split button.
     * @param rect
     * @private
     */
    private getXPosition(rect: any): number {
        const padding = this.splitAction ? 8 : 0;

        return window.innerWidth - rect.x - ((this.displayAsButton || this.splitAction) ? rect.width : 0) - padding;
    }

    /**
     * Set up page subscribers
     * @private
     */
    private setupSubscribers(): void {
        // Wait for emitter to report the new state.
        this.loadingService.loadingActionDotsEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (state: boolean): void => {
                    this.isOpen = state;
                    this.isLoading = state;
                    this.cdr.detectChanges();
                }
            });

        this.contentInjectionService.closeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (closed: boolean): void => {
                    this.isOpen = !closed;
                }
            });
    }
}
